import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Send, Mail, Phone } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { CONTACT_PAGE } from '../data/legalContent';
import { useLanguage } from '../contexts/LanguageContext';
import { authFetch } from '../utils/api';

const ContactPage = ({ lang }) => {
  const navigate = useNavigate();
  const { language, setLanguage } = useLanguage();
  const t = CONTACT_PAGE[lang];

  useEffect(() => {
    if (language !== lang && typeof setLanguage === 'function') setLanguage(lang);
  }, [lang, language, setLanguage]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [lang]);

  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [status, setStatus] = useState('idle');

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    try {
      const res = await authFetch('/api/contact', {
        method: 'POST',
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('failed');
      setStatus('success');
      setForm({ name: '', email: '', subject: '', message: '' });
      setTimeout(() => setStatus('idle'), 5000);
    } catch {
      setStatus('error');
    }
  };

  const inputClass = 'w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/40 focus:outline-none focus:border-cyan-400/50 focus:ring-1 focus:ring-cyan-400/30 transition-colors';
  const backLabel = lang === 'el' ? 'Επιστροφή στην αρχική' : 'Back to home';
  const updatedLabel = lang === 'el' ? 'Τελευταία ενημέρωση' : 'Last updated';

  return (
    <div className="min-h-screen bg-[#050811] text-white">
      <Navbar />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-16" data-testid={`contact-page-${lang}`}>
        <button
          onClick={() => navigate('/')}
          className="text-cyan-400 hover:text-cyan-300 text-sm mb-6 inline-flex items-center transition-colors"
          data-testid="contact-back-home"
        >
          ← {backLabel}
        </button>

        <h1 className="text-4xl md:text-5xl font-bold mb-2">{t.title}</h1>
        <p className="text-white/50 text-sm mb-8">{updatedLabel}: {t.lastUpdated}</p>
        <p className="text-white/80 leading-relaxed mb-10">{t.intro}</p>

        {/* Email channels */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          {t.emails.map((row) => (
            <a
              key={row.email}
              href={`mailto:${row.email}`}
              className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-cyan-400/40 rounded-xl p-5 transition-colors block"
              data-testid={`contact-email-${row.email}`}
            >
              <Mail className="text-cyan-400 mb-3" size={20} />
              <p className="text-white/60 text-xs uppercase tracking-wide mb-1">{row.label}</p>
              <p className="text-white text-sm font-medium break-all">{row.email}</p>
            </a>
          ))}
        </div>

        {/* Phone */}
        <a
          href={`tel:${t.phone.replace(/\s/g, '')}`}
          data-testid="contact-phone-card"
          className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 hover:border-cyan-400/40 rounded-xl p-5 transition-colors block mb-12"
        >
          <Phone className="text-cyan-400 mb-3" size={20} />
          <p className="text-white/60 text-xs uppercase tracking-wide mb-1">{t.phoneLabel}</p>
          <p className="text-white text-sm font-medium">{t.phone}</p>
        </a>

        {/* Form */}
        <h2 className="text-2xl font-semibold mb-6">{t.formTitle}</h2>
        <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-page-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <input
              data-testid="contact-page-name"
              type="text"
              name="name"
              required
              placeholder={t.fields.name}
              value={form.name}
              onChange={handleChange}
              className={inputClass}
            />
            <input
              data-testid="contact-page-email"
              type="email"
              name="email"
              required
              placeholder={t.fields.email}
              value={form.email}
              onChange={handleChange}
              className={inputClass}
            />
          </div>
          <input
            data-testid="contact-page-subject"
            type="text"
            name="subject"
            placeholder={t.fields.subject}
            value={form.subject}
            onChange={handleChange}
            className={inputClass}
          />
          <textarea
            data-testid="contact-page-message"
            name="message"
            required
            rows={6}
            placeholder={t.fields.message}
            value={form.message}
            onChange={handleChange}
            className={`${inputClass} resize-none`}
          />
          <div className="text-center">
            <Button
              data-testid="contact-page-submit"
              type="submit"
              size="lg"
              disabled={status === 'sending'}
              className="bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600 text-white px-10"
            >
              {status === 'sending' ? t.sending : t.submit}
              <Send className="ml-2" size={18} />
            </Button>
          </div>
          {status === 'success' && (
            <p data-testid="contact-page-success" className="text-center text-emerald-400 text-sm mt-4">{t.success}</p>
          )}
          {status === 'error' && (
            <p data-testid="contact-page-error" className="text-center text-red-400 text-sm mt-4">{t.error}</p>
          )}
        </form>
      </main>
      <Footer />
    </div>
  );
};

export default ContactPage;
